import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { calcInvestmentDetails } from 'stores/financeUtils';
import { ITableItem, ITableSortBy } from 'stores/interfaces';
import { IOrder } from 'stores/networkInterfaces';
import { useStore } from 'stores/store';
import { reportEvent } from 'utils/analytics';
import { IFilter, IInvestment } from 'utils/globalTypes';
import { useMobileDetect } from 'utils/hooks/useMobileDetect';
import { useUserAuth } from 'utils/hooks/useUserAuth';
import { IQuery, ISearch, paginationRequest } from 'utils/networkRequests';
import { getGalleryItems } from 'utils/object';
import { filtersFromUrlQuery, filtersToUrlQuery, PAGE_SIZE, QueryParams } from 'utils/query';
import { getSingleValue } from 'utils/urlUtils';

export const useSearchApi = <T extends ITableItem = IInvestment>({
  requestCmd,
  filter,
  defaultFilter,
  updateFilters,
  offeringId,
  limit,
  offset,
  orderField,
  orderDesc,
  selectedField = 'id',
  searchOnInit = true
}: {
  requestCmd: string;
  filter?: Record<string, unknown>;
  defaultFilter?: Record<string, unknown>;
  updateFilters?: (filters?: IFilter[]) => Record<string, unknown>;
  offeringId?: string;
  limit?: number;
  offset?: number;
  orderField?: string;
  orderDesc?: boolean;
  selectedField?: string;
  searchOnInit?: boolean;
}) => {
  const store = useStore();
  const router = useRouter();
  const { showBoundary: handleError } = useErrorBoundary();

  const { isMobile, isTablet } = useMobileDetect();
  const { isAuthenticated, isApproved, isManagement, isSystem, isSystemRW } = useUserAuth();

  const [page, setPage] = useState<number>();
  const [queryReady, setQueryReady] = useState<boolean>(false);
  const [filterByDefault, setFilterByDefault] = useState(defaultFilter);
  const [query, setQuery] = useState<IQuery>({
    filter: filter,
    offset: offset,
    limit: limit || PAGE_SIZE,
    offering_id: offeringId,
    user_filter: undefined,
    offering_filter: undefined,
    order_by_field: orderField,
    order_by_desc: orderDesc
  });
  const [items, setItems] = useState<{ total: number; data: (T | IInvestment)[] }>({
    total: 0,
    data: []
  });
  const [promotion, setPromotion] = useState<T | IInvestment>();
  const [activeOrders, setActiveOrders] = useState<Array<IOrder>>();
  const [isLoading, setLoading] = useState(true);
  const [isSearching, setSearching] = useState(false);

  const setFilter = useCallback((filter?: Record<string, unknown>) => {
    setQuery((prevQuery) => ({
      ...prevQuery,
      filter: filter,
      offset: 0,
      loadMore: false,
      refresh: false
    }));
  }, []);

  const setDefaultFilter = useCallback((filter?: Record<string, unknown>) => {
    setFilterByDefault(filter);
  }, []);

  const setQueryUrl = useCallback(
    (params: QueryParams) => {
      const urlQuery = filtersToUrlQuery(params);
      router?.replace(
        {
          pathname: router?.pathname,
          query: urlQuery
        },
        undefined,
        { shallow: true }
      );
    },
    [router]
  );

  useEffect(() => {
    if (!router?.isReady) return;
    const urlQuery = filtersFromUrlQuery(router?.query);
    store.MarketplaceStore.setOfferingSearchTerm(urlQuery?.offeringTerm);
    store.MarketplaceStore.setFilters(urlQuery?.filters);
  }, [router?.isReady, router?.query, store.MarketplaceStore]);

  useEffect(() => {
    if (!router?.isReady || queryReady) {
      return;
    }

    store.MarketplaceStore.clearStore();

    const urlQuery = filtersFromUrlQuery(router?.query);
    const selectedId =
      selectedField && router?.query ? getSingleValue(router?.query[selectedField]) : undefined;

    if (urlQuery?.offeringTerm) {
      store.MarketplaceStore.setOfferingSearchTerm(urlQuery.offeringTerm);
    }

    if (urlQuery?.filters) {
      store.MarketplaceStore.setFilters(urlQuery.filters);
    }

    const urlFilter = updateFilters && updateFilters(urlQuery?.filters);
    const updatedFilter = { ...(filter || {}), ...(urlFilter || {}) };
    setFilter(updatedFilter);

    setQuery({
      selected_id: selectedId,
      filter: updatedFilter,
      offset: offset,
      limit: limit || PAGE_SIZE,
      offering_id: offeringId,
      user_filter: urlQuery?.userTerm,
      offering_filter: urlQuery?.offeringTerm,
      order_by_field: orderField || urlQuery?.sortField,
      order_by_desc: urlQuery?.sortField ? urlQuery?.sortDesc : orderDesc,
      loadMore: false,
      refresh: false
    });

    setQueryReady(true);
  }, [
    filter,
    limit,
    offeringId,
    offset,
    orderDesc,
    orderField,
    queryReady,
    router?.isReady,
    router?.query,
    selectedField,
    setFilter,
    store.MarketplaceStore,
    updateFilters
  ]);

  useEffect(() => {
    if (query && queryReady && searchOnInit) {
      setSearching(true);

      setQueryUrl({
        term: store.MarketplaceStore.getOfferingSearchTerm,
        userTerm: query?.user_filter,
        filters: store.MarketplaceStore.getFilters,
        sortField: query?.order_by_field,
        sortDesc: query?.order_by_desc
      });

      const queryBody = {
        requestCmd: requestCmd,
        filter: { ...filterByDefault, ...query?.filter },
        selected_id: query?.selected_id,
        offset: query?.offset,
        limit: query?.limit,
        offering_id: query?.offering_id,
        user_filter: query?.user_filter,
        offering_filter: store.MarketplaceStore.getOfferingSearchTerm,
        order_by_field: query?.order_by_field,
        order_by_desc: query?.order_by_desc
      };

      paginationRequest(queryBody)
        .then((response) => {
          if (response?.offset !== undefined) {
            setPage(Math.floor(response?.offset / query?.limit));
          }
          if (response?.data) {
            setItems((prevState) => {
              const data = response?.data?.map((item) =>
                calcInvestmentDetails({
                  ...item,
                  gallery_media: getGalleryItems(item.media)
                })
              );
              return {
                data: query?.refresh
                  ? prevState.data?.map((row) => {
                      const existingItem = data.filter((item) => item?.id === row?.id)[0];
                      if (existingItem) {
                        return { ...row, ...existingItem };
                      }
                      return row;
                    })
                  : query?.offset && query?.loadMore
                  ? prevState.data.concat(data)
                  : data,
                total: query?.refresh ? items?.total : response?.total
              };
              // as { total: number; data: T[] }
            });
          }
          if (response?.promotion) {
            setPromotion(response?.promotion);
          }
          if (response?.active_orders) {
            setActiveOrders(response?.active_orders);
          }
        })
        .catch((e) => handleError(e))
        .finally(() => {
          setLoading(false);
          setSearching(false);
        });
    }
  }, [
    requestCmd,
    searchOnInit,
    isApproved,
    query,
    queryReady,
    store.MarketplaceStore,
    filterByDefault
  ]);

  const onSetOffset = useCallback(
    (offset: number) => {
      if (query) {
        reportEvent('change_offset');
        setQuery({
          ...query,
          offset: offset,
          selected_id: undefined,
          loadMore: false,
          refresh: false
        });
      }
    },
    [query]
  );

  const onSetLimit = useCallback(
    (limit: number) => {
      if (query) {
        reportEvent('change_limit');
        setQuery({
          ...query,
          offset: query?.offset ? Math.floor(query?.offset / limit) * limit : undefined,
          limit: limit,
          loadMore: false,
          refresh: false
        });
      }
    },
    [query]
  );

  const onLoadMore = useCallback(() => {
    if (query) {
      reportEvent('load_more');
      setQuery({
        ...query,
        offset: query?.offset ? query?.offset + (limit || PAGE_SIZE) : undefined,
        selected_id: undefined,
        loadMore: true,
        refresh: false
      });
    }
  }, [query, limit]);

  const onSearch = useCallback(
    (search?: ISearch) => {
      setQuery((prevQuery) => ({
        ...prevQuery,
        offering_id: search?.offeringId,
        user_filter: search?.userSearchTerm,
        offering_filter: store.MarketplaceStore.offeringSearchTerm,
        offset: 0,
        loadMore: false,
        refresh: false
      }));
    },
    [store.MarketplaceStore.offeringSearchTerm]
  );

  const onRefresh = useCallback((filter?: Record<string, unknown>) => {
    setQuery((prevQuery) => ({
      ...prevQuery,
      filter: filter,
      loadMore: false,
      refresh: true
    }));
  }, []);

  const setItemsFilteredById = (id: string) => {
    setItems((prevState) => {
      const filtered = prevState.data.filter((item) => item.id !== id);
      return {
        data: filtered,
        total: prevState.total - 1
      };
    });
  };

  const onSortOrder = useCallback(
    (sortBy?: ITableSortBy) => {
      if (query) {
        if (query?.order_by_field !== sortBy?.id || query?.order_by_desc !== sortBy?.desc) {
          setQuery({
            ...query,
            order_by_field: sortBy?.id,
            order_by_desc: sortBy?.desc,
            selected_id: undefined,
            loadMore: false,
            refresh: false
          });
        }
      }
    },
    [query]
  );

  const setFilters = useCallback(
    (filters?: IFilter[]) => {
      setFilter(updateFilters ? updateFilters(filters) : undefined);
    },
    [setFilter, updateFilters]
  );

  return {
    isAuthenticated,
    isApproved,
    isManagement,
    isSystem,
    isSystemRW,
    isMobile,
    isTablet,
    queryReady,
    isLoading,
    setLoading,
    isSearching,
    query,
    page,
    items,
    promotion,
    activeOrders,
    handleError,
    setQueryUrl,
    onLoadMore,
    onSetOffset,
    onSetLimit,
    onSortOrder,
    onSearch,
    onRefresh,
    setFilter,
    setDefaultFilter,
    setFilters,
    setItemsFilteredById
  };
};
